/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useEffect, useState } from 'react';
import { GalleryContext } from '../context/Gallery.context';
import { useFetchdata } from './hooks/useFetchData';
import { useScrollEvent } from './hooks/useScrollEvent';
import { handleClick } from './services/handleClick';
import { setCounter } from './services/setCounter';
import { WidgetContainerStyled } from '../../styles';
import { ToolbarComponent, ItemsDirective, ItemDirective} from '@syncfusion/ej2-react-navigations';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { GalleryCounter, GalleryGrid, GalleryWrapper, ToolbarWrapper } from '../styles';
import { SortableGalleryItem } from './components/Gallery.card.sort';
import { run as runActions } from '../../../../store/workspaces/workspaces/run';


const GalleryWidget = () => {
  const { imageData, number, targetDivRef, navData, actionsState } = useContext(GalleryContext);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [localImages, setLocalImages] = useState<any[]>([]);
  const baseCardSize = 200;
  const zoomedSize = baseCardSize * zoomLevel;
  const enableDragAndDrop = !navData.widgetData?.filter && navData.widgetData.events.some((event) => event.type === 'onDrop') && navData.widgetData.events.some((event) => event.type === 'onDrag');

  useFetchdata();
  useScrollEvent();

  useEffect(() => {
    if (imageData) setLocalImages(imageData.slice(0, number));
  }, [imageData, number]);

  const handleZoomIn = () => setZoomLevel((prev) => Math.min(prev + 0.35, 5));
  const handleZoomOut = () => setZoomLevel((prev) => Math.max(prev - 0.35, 0.2));
  const handleZoomReset = () => setZoomLevel(1);

  const handleToolbarClick = (args: any) => {
    switch (args.item.id) {
      case 'zoomIn': handleZoomIn(); break;
      case 'zoomOut': handleZoomOut(); break;
      case 'zoomMax': setZoomLevel(5); break;
      case 'zoomMin': setZoomLevel(0.2); break;
      case 'zoomReset': handleZoomReset(); break;
      default: break;
    }
  };

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = async(event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = localImages.findIndex(i => i.id === active.id);
    const newIndex = localImages.findIndex(i => i.id === over.id);
    const moved = (arr: any[], i: number) => arr[i] ? { id: arr[i].id, index: i } : null;

    const before = {
      onDrag: {
        this: moved(localImages, oldIndex),
        prev: moved(localImages, oldIndex - 1),
        next: moved(localImages, oldIndex + 1)
      },
      onDrop: {}
    };

    const onDragEvent = navData.widgetData.events.find((event) => event.type === 'onDrag')?.key;
    await runActions(onDragEvent, null, { event: before, ...actionsState });

    const newImages = arrayMove(localImages, oldIndex, newIndex);
    setLocalImages(newImages);

    const after = {
      onDrop: {
        this: moved(newImages, newIndex),
        prev: moved(newImages, newIndex - 1),
        next: moved(newImages, newIndex + 1),
      },
      onDrag: before.onDrag
    };
    const onDropEvent = navData.widgetData.events.find((event) => event.type === 'onDrop')?.key;
    await runActions(onDropEvent, null, { event: after, ...actionsState });
  };

  if (!imageData) return null;

  return (
    <WidgetContainerStyled>
      <ToolbarWrapper>
        <ToolbarComponent
          id="zoom-toolbar"
          clicked={handleToolbarClick}
          style={{ backgroundColor: 'white' }}
        >
          <ItemsDirective>
            <ItemDirective prefixIcon="e-icons e-zoom-in" tooltipText="Zoom In" id="zoomIn" />
            <ItemDirective prefixIcon="e-icons e-zoom-out" tooltipText="Zoom Out" id="zoomOut" />
            <ItemDirective tooltipText="Max Zoom" id="zoomMax" text="Max" />
            <ItemDirective tooltipText="Min Zoom" id="zoomMin" text="Min" />
            <ItemDirective tooltipText="Reset Zoom" id="zoomReset" text="Reset" />
          </ItemsDirective>
        </ToolbarComponent>
      </ToolbarWrapper>

      <GalleryWrapper ref={targetDivRef}>
        {enableDragAndDrop ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={localImages.map((img) => img.id)}
              strategy={rectSortingStrategy}
            >
              <GalleryGrid itemsize={zoomedSize}>
                {localImages.map((image) => (
                  <SortableGalleryItem
                    key={image.id}
                    image={image}
                    zoomLevel={zoomLevel}
                    onClick={() => handleClick(image.id, navData, actionsState)}
                  />
                ))}
              </GalleryGrid>
            </SortableContext>
          </DndContext>
        ) : (
          <GalleryGrid itemsize={zoomedSize}>
            {localImages.map((image) => (
              <SortableGalleryItem
                key={image.id}
                image={image}
                zoomLevel={zoomLevel}
                onClick={() => handleClick(image.id, navData, actionsState)}
              />
            ))}
          </GalleryGrid>
        )}
      </GalleryWrapper>

      <GalleryCounter>
        {setCounter(localImages)}
      </GalleryCounter>
    </WidgetContainerStyled>
  );
};

export default GalleryWidget;
